import { Component, inject, input, OnInit, signal } from "@angular/core";
import { FormBuilder, FormGroup, ReactiveFormsModule, Validators } from "@angular/forms";
import { Router } from "@angular/router";
import { TranslatePipe, TranslateService } from "@ngx-translate/core";
import { ToastrService } from "ngx-toastr";

import { AdaaHelper } from "../../../../core/utils";
import { dateRangeValidator } from "../../../../core/validators";
import {
  FormActionButtonsComponent,
  FormCheckboxComponent,
  FormDropdownComponent,
  FormInputComponent,
  FormInputFileComponent,
} from "../../../../shared/components";
import { Constants } from "../../../../shared/constants/constants";
import { FileInputType, PageMode } from "../../../../shared/constants/enums";
import { AttachFile, ValidatorModelType, ValueText } from "../../../../shared/models";
import { Faq } from "../../../../shared/models/online-help-model";
import { ValidatorApiService } from "../../../../shared/services";
import { OnlineHelpService } from "../../../../shared/services/online-help-service";

@Component({
  selector: "adaa-faqs-editor",
  imports: [
    TranslatePipe,
    FormDropdownComponent,
    FormInputComponent,
    FormActionButtonsComponent,
    ReactiveFormsModule,
    FormInputFileComponent,
    FormCheckboxComponent,
  ],
  templateUrl: "./faqs-editor.html",
  styleUrl: "./faqs-editor.scss",
})
export class FaqsEditorComponent implements OnInit {
  private _formBuilder = inject(FormBuilder);
  private _translateService = inject(TranslateService);
  private _toastrService = inject(ToastrService);
  private _onlineHelpService = inject(OnlineHelpService);
  private _router = inject(Router);
  private _validatorApiService = inject(ValidatorApiService);

  id = input<string>();

  formValidation = signal<ValidatorModelType | undefined>(undefined);

  faqsForm: FormGroup = new FormGroup({});
  displayLabel = signal<string>("");
  submitted = signal<boolean>(false);
  pageMode = input.required<string>();
  untilDestroy = AdaaHelper.untilDestroyed();
  PageMode = PageMode;
  categories = signal<ValueText[]>([]);
  faq = signal<Faq | undefined>(undefined);
  allowedFiles = [
    FileInputType.pdf,
    FileInputType.doc,
    FileInputType.docx,
    FileInputType.xls,
    FileInputType.xlsx,
    FileInputType.ppt,
    FileInputType.pptx,
    FileInputType.txt,
    FileInputType.jpeg,
    FileInputType.jpg,
    FileInputType.png,
    FileInputType.gif,
    FileInputType.svg,
  ];

  public ngOnInit(): void {
    const id = this.id();
    if (id) {
      this._onlineHelpService.getFAQ(+id).subscribe((response) => {
        if (response.inError) return;
        this.faq.set(response.responseData);
        this.faqsForm.patchValue(response.responseData);
      });
    }

    this._onlineHelpService.getAllFAQCategories().subscribe((response) => {
      this.categories.set(
        AdaaHelper.setDropdownArray(response.responseData ?? [], "id", AdaaHelper.getFieldLanguage("name"))
      );
    });
    this._prepareForm();
  }

  private _prepareForm() {
    const isViewMode = this.pageMode() === PageMode.view;

    this.faqsForm = this._formBuilder.group(
      {
        categoryId: { value: null, disabled: isViewMode },
        onlineHelpLinkAE: { value: null, disabled: isViewMode },
        onlineHelpLinkEN: { value: null, disabled: isViewMode },
        questionAE: { value: null, disabled: isViewMode },
        questionEN: { value: null, disabled: isViewMode },
        answerAE: { value: null, disabled: isViewMode },
        answerEN: { value: null, disabled: isViewMode },
        videoLinkAE: { value: null, disabled: isViewMode },
        videoLinkEN: { value: null, disabled: isViewMode },
        attachment: { value: undefined, disabled: isViewMode },
        published: { value: null, disabled: isViewMode },
      },
      {
        validators: Validators.compose([dateRangeValidator("startYear", "endYear")]),
      }
    );

    this._validatorApiService
      .applyValidationToForm(Constants.VALIDATORS_CONF_KEY.VALIDATION_FAQS, this.faqsForm)
      .subscribe({
        next: (response) => {
          this.formValidation.set(response.validation);
          this.faqsForm = response.form;
        },
      });
  }

  public checkInvalid(control: string) {
    return (
      this.faqsForm.controls[control].errors?.["required"] && (this.faqsForm.get(control)?.dirty || this.submitted())
    );
  }

  public submit() {
    this.submitted.set(true);

    if (this.faqsForm.invalid) {
      this._toastrService.error(this._translateService.instant("notification.warning.missing_info"));
      return;
    }

    let result = {
      ...(this.pageMode() === PageMode.edit ? { ...this.faq() } : {}),
      ...this.faqsForm.value,
      categoryId: this.faqsForm.value.categoryId?.toString(),
      status: Constants.OBJECT_STATUS.ACTIVE,
    };
    result = Object.fromEntries(Object.entries(result).filter(([_, value]) => value !== null));

    const url =
      this.pageMode() === PageMode.create
        ? this._onlineHelpService.createFAQ(result)
        : this._onlineHelpService.updateFAQ(result);

    url.subscribe({
      next: (response) => {
        if (response.inError) return;
        this._toastrService.success(this._translateService.instant("notification.success.save"));
        this.close();
      },
    });
  }

  public close() {
    this._router.navigateByUrl("/console/help/faqs");
  }

  public getImage() {
    const attach = this.faqsForm.get("attachment")?.value;
    return {
      id: attach?.id ?? 0,
      filename: attach?.filename ?? "",
    };
  }

  public onFileChange(event: AttachFile | null) {
    if (event) {
      this.faqsForm.get("attachment")?.setValue({
        id: event.id,
        attachFile: null,
        filename: event.filename,
        activityId: null,
        taskId: null,
        auxAttachFile: {
          filename: null,
        },
      });
    } else this.faqsForm.get("attachment")?.setValue(null);
  }
}
